// STATUS (2026-09-01): PARKED — founder decision. The KSA guided-keyword-search agent
// this page hosts is broken/half-baked; both UI entry points (SCV2Modals 'EP Patent
// Search — AI Agent', SotaSearchPanel 'ksa' option) are greyed out. Kept as an idea
// source for a future eval-first search-agent rebuild (.context/AGENTIC_ACTION_PLAN_2026-08.md).
// Do NOT delete in dead-code sweeps; do NOT re-enable without a fix.
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import type { SubProject } from '../services/subProjectService';
import { subProjectService } from '../services/subProjectService';
import type { CreateSearchSessionRequest } from '../services/searchService';
import { searchService } from '../services/searchService';
import type { AgentMessage } from '../services/patentSearchChatService';
import { patentSearchChatService } from '../services/patentSearchChatService';
import { semanticSearchService } from '../services/semanticSearchService';
import apiClient from '../services/api';
import useFeaturesListStore from '../stores/featuresListStore';
import useSemanticSearchStore from '../stores/semanticSearchStore';
import { useTranslation } from 'react-i18next';
import PageHeader from '../components/layout/PageHeader';
import ChatMessage from '../components/ChatMessage';
import ChatInput from '../components/ChatInput';
import FeaturesTable from '../components/FeaturesTable';
import PhaseProgressPanel from '../components/PhaseProgressPanel';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';

export default function SemanticSearchPage() {
  const { subProjectId } = useParams<{ subProjectId: string }>();
  const [searchParams] = useSearchParams();
  const [subProject, setSubProject] = useState<SubProject | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [conversation, setConversation] = useState<AgentMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [initializing, setInitializing] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Zustand stores
  const loadFeaturesList = useFeaturesListStore((state) => state.loadFeaturesList);
  const generateFeaturesList = useFeaturesListStore((state) => state.generateFeaturesList);
  const saveFeaturesList = useFeaturesListStore((state) => state.saveFeaturesList);
  const features = useFeaturesListStore((state) => state.features);
  const isGenerating = useFeaturesListStore((state) => state.isGenerating);
  const initializeFeaturesList = useFeaturesListStore((state) => state.initializeFeaturesList);
  const selectAllFeatures = useFeaturesListStore((state) => state.selectAllFeatures);
  const deselectAllFeatures = useFeaturesListStore((state) => state.deselectAllFeatures);

  const initializeSearch = useSemanticSearchStore((state) => state.initializeSearch);

  // KSearch coverage tracking
  const currentPhase = useSemanticSearchStore((state) => state.currentPhase);
  const kSearchCoverage = useSemanticSearchStore((state) => state.kSearchCoverage);
  const searchCompleteness = useSemanticSearchStore((state) => state.searchCompleteness);
  const innerLoopActive = useSemanticSearchStore((state) => state.innerLoopActive);
  const updateKSearchCoverage = useSemanticSearchStore((state) => state.updateKSearchCoverage);
  const setSearchCompleteness = useSemanticSearchStore((state) => state.setSearchCompleteness);
  const setPhase = useSemanticSearchStore((state) => state.setPhase);
  const startInnerLoop = useSemanticSearchStore((state) => state.startInnerLoop);
  const completeInnerLoop = useSemanticSearchStore((state) => state.completeInnerLoop);

  // Acquisition tracking
  const acquisitionStatus = useSemanticSearchStore((state) => state.acquisitionStatus);
  const acquisitionProgress = useSemanticSearchStore((state) => state.acquisitionProgress);
  const setAcquisitionStatus = useSemanticSearchStore((state) => state.setAcquisitionStatus);
  const setAcquisitionProgress = useSemanticSearchStore((state) => state.setAcquisitionProgress);

  // Acquisition polling handle — cleared on completion/failure AND on unmount.
  const acquisitionPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const conversationPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    return () => {
      if (acquisitionPollRef.current) clearInterval(acquisitionPollRef.current);
      if (conversationPollRef.current) clearInterval(conversationPollRef.current);
    };
  }, []);

  // KList status tracking (for Phase 1 -> Phase 2 transition)
  const [klistTotal, setKlistTotal] = useState(0);
  const [ksearchSignaledComplete, setKsearchSignaledComplete] = useState(false);

  useEffect(() => {
    if (subProjectId) {
      loadSubProject();
      initializeStores();

      // Check if loading existing session or creating new one
      const existingSessionId = searchParams.get('sessionId');
      if (existingSessionId) {
        handleLoadExistingSession(existingSessionId);
      } else {
        handleStartSession();
      }
    }
  }, [subProjectId, searchParams]);

  useEffect(() => {
    scrollToBottom();
  }, [conversation]);

  // Poll KList status during KSearch phase
  useEffect(() => {
    if (!sessionId || currentPhase === 'acquisition') {
      return;
    }

    const pollKlistStatus = async () => {
      try {
        const response = await apiClient.get(`/search-sessions/${sessionId}/klist-status`);
        const { total_patents, ksearch_signaled_complete } = response.data;
        setKlistTotal(total_patents);
        setKsearchSignaledComplete(ksearch_signaled_complete);

        // Auto-transition to ksearch_complete when KSA signals completion
        if (ksearch_signaled_complete && currentPhase === 'ksearch') {
          completeInnerLoop();
          setPhase('ksearch_complete');
        }
      } catch (err) {
        console.error('Error polling KList status:', err);
      }
    };

    // Poll immediately and then every 3 seconds
    pollKlistStatus();
    const interval = setInterval(pollKlistStatus, 3000);
    return () => clearInterval(interval);
  }, [sessionId, currentPhase, completeInnerLoop, setPhase]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const initializeStores = () => {
    if (!subProjectId) return;
    initializeFeaturesList(subProjectId);
    initializeSearch(subProjectId);
    loadFeaturesList(subProjectId);
  };

  const loadSubProject = async () => {
    if (!subProjectId) return;
    try {
      setLoading(true);
      const subProj = await subProjectService.getById(subProjectId);
      setSubProject(subProj);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load sub-project');
    } finally {
      setLoading(false);
    }
  };

  const handleStartSession = async () => {
    if (!subProjectId) return;

    try {
      setInitializing(true);
      setError(null);

      const createData: CreateSearchSessionRequest = {
        sub_project_id: subProjectId,
        session_name: `Semantic Search - ${new Date().toLocaleString()}`,
        description: 'Semantic patent search session with RAG',
        search_service: 'epo_espacenet',
        search_query: {
          query_string: 'semantic_search_managed',
          max_results: 1000,
          language: 'en',
          date_range_start: null,
          date_range_end: null,
          country_codes: [],
          classification_codes: []
        }
      };

      const newSession = await searchService.createSession(createData);
      setSessionId(newSession.id);

      await patentSearchChatService.startSession(newSession.id);
      setInitializing(false);

      await loadConversation(newSession.id);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start session');
      setInitializing(false);
    }
  };

  const handleLoadExistingSession = async (existingSessionId: string) => {
    if (!subProjectId) return;

    try {
      setInitializing(true);
      setError(null);

      console.log('Loading existing session:', existingSessionId);

      // Set session ID immediately
      setSessionId(existingSessionId);

      // Load the conversation history
      await loadConversation(existingSessionId);

      // Load session data to get metadata and parse coverage
      const sessionResponse = await apiClient.get(`/search-sessions/${existingSessionId}`);
      const sessionData = sessionResponse.data;

      console.log('Loaded session data:', {
        name: sessionData.session_name,
        queries: sessionData.query_table?.length,
        totalResults: sessionData.total_results_found
      });

      // Parse coverage from query table
      if (sessionData.query_table && sessionData.query_table.length > 0) {
        const totalPatents = sessionData.query_table.reduce(
          (sum: number, q: { returned_patent_ids?: string[] }) => sum + (q.returned_patent_ids?.length || 0),
          0
        );

        updateKSearchCoverage({
          total_patents: totalPatents,
          cpc_classes_found: [],
          keyword_strategies_explored: []
        });

        // If searches were completed, set phase to complete
        if (sessionData.search_phase === 'completed' || sessionData.query_table.length >= 5) {
          setPhase('ksearch_complete');
          setSearchCompleteness('comprehensive');
        }
      }

      setInitializing(false);

    } catch (err) {
      console.error('Error loading existing session:', err);
      setError(err instanceof Error ? err.message : 'Failed to load session');
      setInitializing(false);
    }
  };

  const handleSendMessage = async (message: string) => {
    if (!sessionId) return;

    try {
      setSending(true);
      setError(null);

      setConversation(prev => [...prev, {
        role: 'user',
        content: message,
        timestamp: new Date().toISOString(),
        metadata: {}
      }]);

      // Handle kept in a ref so navigating away mid-send clears it (unmount cleanup above).
      if (conversationPollRef.current) clearInterval(conversationPollRef.current);
      conversationPollRef.current = setInterval(async () => {
        await loadConversation(sessionId);
      }, 1000);

      patentSearchChatService.sendMessage(sessionId, message).then(() => {
        if (conversationPollRef.current) clearInterval(conversationPollRef.current);
        loadConversation(sessionId);
        setSending(false);
      }).catch((err) => {
        if (conversationPollRef.current) clearInterval(conversationPollRef.current);
        setError(err instanceof Error ? err.message : 'Failed to send message');
        setSending(false);
      });

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send message');
      setSending(false);
    }
  };

  const parseKSearchCoverage = useCallback((messages: AgentMessage[]) => {
    const lastAssistantMsgs = messages
      .filter(msg => msg.role === 'assistant')
      .slice(-5);

    const coverage: Partial<typeof kSearchCoverage> = {};

    for (const msg of lastAssistantMsgs) {
      const content = msg.content;

      const patentMatch = content.match(/(?:cumulative|total|corpus|identified).*?(\d+)\s*patents?/i);
      if (patentMatch) {
        coverage.total_patents = parseInt(patentMatch[1], 10);
      }

      const cpcMatch = content.match(/CPC\s+(?:classes?|codes?).*?:\s*([A-Z0-9,;\s]+)/i);
      if (cpcMatch) {
        const classes = cpcMatch[1]
          .split(/[,;]/)
          .map(c => c.trim())
          .filter(c => c.length > 0 && /^[A-Z0-9]+$/.test(c));
        if (classes.length > 0) {
          coverage.cpc_classes_found = classes;
        }
      }

      if (content.match(/broad.*?(?:search|strategy|keywords)/i)) {
        coverage.keyword_strategies_explored = coverage.keyword_strategies_explored || [];
        if (!coverage.keyword_strategies_explored.includes('broad')) {
          coverage.keyword_strategies_explored.push('broad');
        }
      }
      if (content.match(/specific.*?(?:search|strategy|keywords)/i) || content.match(/narrow.*?(?:search|strategy)/i)) {
        coverage.keyword_strategies_explored = coverage.keyword_strategies_explored || [];
        if (!coverage.keyword_strategies_explored.includes('specific')) {
          coverage.keyword_strategies_explored.push('specific');
        }
      }
      if (content.match(/combined.*?(?:search|strategy)/i) || content.match(/TA.*?(?:\+|and).*?TXT/i)) {
        coverage.keyword_strategies_explored = coverage.keyword_strategies_explored || [];
        if (!coverage.keyword_strategies_explored.includes('combined')) {
          coverage.keyword_strategies_explored.push('combined');
        }
      }
    }

    return coverage;
  }, []);

  const assessSearchCompleteness = useCallback((coverage: typeof kSearchCoverage): 'incomplete' | 'sufficient' | 'comprehensive' => {
    if (!coverage) return 'incomplete';

    const hasCPCClasses = (coverage.cpc_classes_found?.length || 0) >= 3;
    const hasStrategies = (coverage.keyword_strategies_explored?.length || 0) >= 2;
    const hasPatents = (coverage.total_patents || 0) >= 50;

    if (hasCPCClasses && hasStrategies && hasPatents) {
      return 'comprehensive';
    } else if (hasPatents && (hasCPCClasses || hasStrategies)) {
      return 'sufficient';
    } else {
      return 'incomplete';
    }
  }, []);

  // Memoize expensive coverage parsing - only recompute when conversation changes
  const memoizedParsedCoverage = useMemo(() => {
    return parseKSearchCoverage(conversation);
  }, [conversation, parseKSearchCoverage]);

  // Update coverage in store when memoized value changes.
  // kSearchCoverage is intentionally excluded from deps: it is written by updateKSearchCoverage,
  // so including it would cause an infinite update loop.
  useEffect(() => {
    if (memoizedParsedCoverage && Object.keys(memoizedParsedCoverage).length > 0) {
      updateKSearchCoverage(memoizedParsedCoverage);
      const newCompleteness = assessSearchCompleteness(memoizedParsedCoverage as Parameters<typeof assessSearchCompleteness>[0]);
      setSearchCompleteness(newCompleteness);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memoizedParsedCoverage]);

  const loadConversation = async (sid: string) => {
    try {
      const response = await patentSearchChatService.getConversation(sid);
      setConversation(response.conversation);
      // Coverage parsing now happens automatically via memoized value above

      // Detect inner loop start from assistant messages (for UI feedback only)
      const lastMsg = response.conversation[response.conversation.length - 1];
      if (lastMsg && lastMsg.role === 'assistant') {
        const content = lastMsg.content.toLowerCase();
        if (content.includes('starting inner loop') || content.includes('beginning search') ||
            content.includes('starting ksearch') || content.includes('exploratory phase')) {
          startInnerLoop();
        }
      }
      // Note: KSearch completion is now detected via klist-status API polling, not text detection
    } catch (err) {
      console.error('Failed to load conversation:', err);
    }
  };

  const handleGenerateFeatures = useCallback(async () => {
    await generateFeaturesList();
  }, [generateFeaturesList]);

  const handleSaveFeatures = useCallback(async () => {
    await saveFeaturesList();
  }, [saveFeaturesList]);

  const handleProceedToSemanticSearch = useCallback(async () => {
    if (!sessionId || !subProjectId) {
      setError('Session or sub-project not initialized');
      return;
    }

    try {
      setError(null);
      setPhase('acquisition');

      // Step 1: Get patent IDs from session
      const idsResponse = await apiClient.get(`/search-sessions/${sessionId}/patent-ids`);
      const patentIds = idsResponse.data.patent_ids;

      if (!patentIds || patentIds.length === 0) {
        setError('No patent IDs found in session');
        return;
      }

      console.log(`Starting acquisition for ${patentIds.length} patents`);

      // Step 2: Start acquisition
      setAcquisitionStatus('in_progress');
      const acquisitionResponse = await semanticSearchService.acquirePatents(
        subProjectId,
        patentIds
      );

      console.log('Acquisition started:', acquisitionResponse);

      // Step 3: Poll acquisition progress (handle kept in a ref so unmount clears it)
      acquisitionPollRef.current = setInterval(async () => {
        try {
          const statusResponse = await semanticSearchService.getAcquisitionStatus(subProjectId);

          if (statusResponse.data) {
            setAcquisitionProgress(statusResponse.data);

            if (statusResponse.status === 'completed') {
              if (acquisitionPollRef.current) clearInterval(acquisitionPollRef.current);
              setAcquisitionStatus('completed');
              console.log('Acquisition completed:', statusResponse.data);
            } else if (statusResponse.status === 'failed') {
              if (acquisitionPollRef.current) clearInterval(acquisitionPollRef.current);
              setAcquisitionStatus('failed');
              setError('Acquisition failed');
            }
          }
        } catch (err) {
          console.error('Error polling acquisition status:', err);
        }
      }, 2000);

    } catch (err) {
      console.error('Error starting acquisition:', err);
      setError(err instanceof Error ? err.message : 'Failed to start acquisition');
      setAcquisitionStatus('failed');
    }
  }, [sessionId, subProjectId, setPhase, setAcquisitionStatus, setAcquisitionProgress]);

  const { t } = useTranslation();

  if (loading || initializing) {
    return (
      <div className="flex flex-col gap-3 p-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-[calc(100vh-18rem)] w-full" />
      </div>
    );
  }

  if (error && !subProject) {
    return <div className="flex items-center justify-center h-64 text-destructive text-sm">{error}</div>;
  }

  const allSelected = features.length > 0 && features.every(f => f.included_in_search);

  const breadcrumbs = subProject
    ? [
        { label: 'Project', href: `/scv2/project/${subProject.project_id}` },
        { label: subProject.name, href: `/scv2/sub-project/${subProjectId}` },
        { label: t('pages.semanticSearch.title') },
      ]
    : [{ label: t('pages.semanticSearch.title') }];

  const isKSearchPhase = currentPhase === 'ksearch' || currentPhase === 'ksearch_complete';

  return (
    <div className="flex flex-col h-full overflow-y-auto">
      <PageHeader title={t('pages.semanticSearch.title')} breadcrumbs={breadcrumbs} />

      <div className="px-4 py-3">
        <PhaseProgressPanel
          currentPhase={currentPhase}
          kSearchPatents={kSearchCoverage?.total_patents || 0}
          acquisitionStatus={acquisitionStatus}
          acquisitionProgress={acquisitionProgress}
        />
      </div>

      <div className={`flex gap-4 px-4 pb-4 ${isKSearchPhase ? 'flex-col' : 'flex-row'}`}>
        {/* Phase 1 Conversation — shown first when in KSearch phase */}
        {isKSearchPhase && (
          <div className="flex flex-col bg-white border border-slate-200 rounded-lg overflow-hidden min-h-[24rem]">
            <div className="flex items-center justify-between px-4 py-2 border-b border-slate-200 bg-slate-50 flex-shrink-0">
              <h3 className="text-sm font-semibold text-slate-700">{t('pages.semanticSearch.phase1')} {t('pages.semanticSearch.conversation')}</h3>
              <div className="flex items-center gap-2">
                <Badge variant="secondary">{t('pages.semanticSearch.sessionActive')}</Badge>
                {kSearchCoverage && kSearchCoverage.total_patents > 0 && (
                  <span className="text-xs text-slate-500">{t('pages.semanticSearch.patentsFound', { count: kSearchCoverage.total_patents })}</span>
                )}
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-2 min-h-0">
              {conversation.filter(msg => msg.role !== 'system').map((msg, i) => <ChatMessage key={i} message={msg} />)}
              <div ref={messagesEndRef} />
            </div>
            {error && <div className="mx-4 mb-2 text-xs text-destructive">{error}</div>}
            <div className="flex-shrink-0 border-t border-slate-200">
              <ChatInput onSend={handleSendMessage} disabled={sending}
                placeholder={sending ? t('pages.semanticSearch.agentThinking') : t('pages.semanticSearch.ksearchChatPlaceholder')} />
            </div>
          </div>
        )}

        {/* Features Box */}
        <div className="flex flex-col bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2 border-b border-slate-200 bg-slate-50 flex-shrink-0">
            <h3 className="text-sm font-semibold text-slate-700">{t('pages.semanticSearch.features')}</h3>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={handleGenerateFeatures} disabled={isGenerating}>
                {isGenerating ? t('pages.semanticSearch.generating') : t('pages.semanticSearch.extractFeatures')}
              </Button>
              <Button size="sm" variant="outline" onClick={handleSaveFeatures}>{t('pages.semanticSearch.save')}</Button>
              <Button size="sm" variant="outline" onClick={allSelected ? deselectAllFeatures : selectAllFeatures}>
                {allSelected ? t('pages.semanticSearch.deselectAll') : t('pages.semanticSearch.selectAll')}
              </Button>
            </div>
          </div>
          <div className="overflow-y-auto">
            {subProjectId && <FeaturesTable subProjectId={subProjectId} />}
          </div>

          {/* Search actions */}
          <div className="border-t border-slate-200 px-4 py-3 bg-slate-50 flex-shrink-0 space-y-2">
            <div className="flex items-center gap-2 text-xs text-slate-600 flex-wrap">
              <span className="font-medium">{t('pages.semanticSearch.coverage')}</span>
              <span>
                {kSearchCoverage
                  ? `${t('pages.semanticSearch.coverageSummary', { patents: kSearchCoverage.total_patents, cpc: kSearchCoverage.cpc_classes_found.length })}${kSearchCoverage.keyword_strategies_explored.length > 0 ? t('pages.semanticSearch.coverageStrategies', { count: kSearchCoverage.keyword_strategies_explored.length }) : ''}`
                  : t('pages.semanticSearch.readyToStartExploration')}
              </span>
              <span className="font-medium ml-2">{t('pages.semanticSearch.completenessLabel')}</span>
              <Badge variant={searchCompleteness === 'comprehensive' ? 'default' : searchCompleteness === 'sufficient' ? 'secondary' : 'outline'}>
                {t(`pages.semanticSearch.completeness.${searchCompleteness}`)}
              </Badge>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button size="sm"
                disabled={klistTotal === 0 || acquisitionStatus === 'in_progress'}
                onClick={handleProceedToSemanticSearch}
                className={ksearchSignaledComplete ? 'ring-2 ring-primary-400' : ''}
              >
                {acquisitionStatus === 'in_progress'
                  ? t('pages.semanticSearch.acquiringDocuments')
                  : klistTotal > 0
                    ? `${t('pages.semanticSearch.startAcquisition', { count: klistTotal })}${ksearchSignaledComplete ? ' ✓' : ''}`
                    : t('pages.semanticSearch.noPatentsYet')}
              </Button>
              <span className="text-xs text-slate-400">
                {acquisitionStatus === 'in_progress' ? t('pages.semanticSearch.statusMessages.acquiring')
                  : acquisitionStatus === 'completed' ? t('pages.semanticSearch.statusMessages.acquisitionComplete')
                  : ksearchSignaledComplete && klistTotal > 0 ? t('pages.semanticSearch.statusMessages.ksearchComplete', { count: klistTotal })
                  : klistTotal > 0 ? t('pages.semanticSearch.statusMessages.patentsAvailable', { count: klistTotal })
                  : innerLoopActive ? t('pages.semanticSearch.statusMessages.innerLoopInProgress')
                  : t('pages.semanticSearch.statusMessages.buildCorpus')}
              </span>
            </div>
          </div>
        </div>

        {/* Conversation Box — shown BELOW features when not in Phase 1 */}
        {!isKSearchPhase && (
          <div className="flex flex-col bg-white border border-slate-200 rounded-lg overflow-hidden">
            <div className="flex items-center justify-between px-4 py-2 border-b border-slate-200 bg-slate-50 flex-shrink-0">
              <h3 className="text-sm font-semibold text-slate-700">{t('pages.semanticSearch.conversation')}</h3>
              <Badge variant="secondary">{t('pages.semanticSearch.sessionActive')}</Badge>
            </div>
            <div className="flex-1 overflow-y-auto p-2 min-h-[16rem] max-h-[28rem]">
              {conversation.filter(msg => msg.role !== 'system').map((msg, i) => <ChatMessage key={i} message={msg} />)}
              <div ref={messagesEndRef} />
            </div>
            {error && <div className="mx-4 mb-2 text-xs text-destructive">{error}</div>}
            <div className="flex-shrink-0 border-t border-slate-200">
              <ChatInput onSend={handleSendMessage} disabled={sending}
                placeholder={sending ? t('pages.semanticSearch.agentThinking') : t('pages.semanticSearch.refinePlaceholder')} />
            </div>
          </div>
        )}
      </div>

    </div>
  );
}
